import { create } from "zustand";
import { devtools } from "zustand/middleware";
import axios from "axios";

const initialState = {
  payrollTableData: null,
  gettingPayrollTable: false,
  gettingPayrollTableError: {},

  showCreatePayroll: false,
  payrollToEdit: null,
  creating: true,

  showCreatePayrollPeriod: false,
  creatingPayrollPeriod: true,
  payrollPeriodCreated: null,
  currentPayrollPeriod: null,

  payrolls: [],
  gettingPayrolls: false,
  gettingPayrollsError: {},

  payrollDetails: {},
  gettingPayrollDetails: false,
  getPayrollDetailsError: {},
  showPayrollDetails: false,

  creatingPayroll: false,
  creatingPayrollError: {},
  createdPayroll: null,

  updatingPayroll: false,
  updatingPayrollError: {},
  updatedPayroll: null,

  deletingPayroll: false,
  deletingPayrollError: {},
  deletedPayroll: null,

  approvingPayroll: false,
  approvingPayrollError: {},
  approvedPayroll: null,

  checkingPayroll: false,
  checkingPayrollError: {},
  checkedPayroll: null,

  rejectingPayroll: false,
  rejectingPayrollError: {},
  rejectedPayroll: null,

  undoingApprovePayroll: false,
  undoingApprovePayrollError: {},
  undoneApprovePayroll: null,

  downloadingPayrollPdf: false,
  downloadingPayrollPdfError: {},
};

const store = (set, get) => ({
  ...initialState,

  getPayrollTable: async (month, year) => {
    set({ gettingPayrollTable: true, gettingPayrollTableError: {} });
    try {
      const res = await axios.get(`/payroll/table`, {
        params: { month, year },
      });
      const payrollData = res?.data || res;
      set({ payrollTableData: payrollData });
    } catch (error) {
      set({ gettingPayrollTableError: error });
    } finally {
      set({ gettingPayrollTable: false });
    }
  },

  getAllPayrolls: async () => {
    set({ gettingPayrolls: true, gettingPayrollsError: {} });
    try {
      const res = await axios.get("/payroll");
      const responseData = res?.data || res;
      const data = responseData?.data || responseData;
      
      let payrollsData = [];
      if (data?.payroll && Array.isArray(data.payroll)) {
        payrollsData = data.payroll;
      } else if (responseData?.payroll && Array.isArray(responseData.payroll)) {
        payrollsData = responseData.payroll;
      } else if (data && Array.isArray(data)) {
        payrollsData = data;
      } else if (responseData && Array.isArray(responseData)) {
        payrollsData = responseData;
      } else if (res?.payroll && Array.isArray(res.payroll)) {
        payrollsData = res.payroll;
      } else if (Array.isArray(res)) {
        payrollsData = res;
      } else {
        throw new Error("Invalid payroll response structure");
      }
      
      const periods = data?.periods || responseData?.periods || res?.periods || [];
      
      if (periods.length > 0) {
        const periodsMap = new Map();
        payrollsData.forEach(payroll => {
          const key = `${payroll.month}-${payroll.year}`;
          periodsMap.set(key, payroll);
        });
        
        periods.forEach(period => {
          const key = `${period.month}-${period.year}`;
          if (!periodsMap.has(key)) {
            payrollsData.push({
              id: null,
              month: period.month,
              year: period.year,
              payrolls: [],
            });
          }
        });
      }
      
      set({ payrolls: { data: payrollsData, periods } });
    } catch (error) {
      set({ gettingPayrollsError: error });
    } finally {
      set({ gettingPayrolls: false });
    }
  },

  getPayrollDetails: async (id) => {
    set({ gettingPayrollDetails: true, getPayrollDetailsError: {} });
    try {
      const res = await axios.get(`/payroll/${id}`);
      const responseData = res?.data || res;
      const payrollData = responseData?.data || responseData;
      set({ payrollDetails: payrollData });
    } catch (error) {
      set({ getPayrollDetailsError: error });
    } finally {
      set({ gettingPayrollDetails: false });
    }
  },

  createMonthlyPayroll: async (data) => {
    set({ creatingPayroll: true, creatingPayrollError: {}, createdPayroll: null });
    try {
      const res = await axios.post("/payroll", data);
      set({ createdPayroll: res });
      get().getAllPayrolls();
      if (data.month && data.year) {
        get().getPayrollTable(data.month, data.year);
      }
      get().setCreatingPayroll(false, false, null);
    } catch (error) {
      set({ creatingPayrollError: error });
    } finally {
      set({ creatingPayroll: false });
    }
  },

  updateMonthlyPayroll: async (id, data) => {
    set({ updatingPayroll: true, updatingPayrollError: {}, updatedPayroll: null });
    try {
      const res = await axios.put(`/payroll/${id}`, data);
      set({ updatedPayroll: res, updatingPayrollError: {} });
      get().getAllPayrolls();
      if (data.month && data.year) {
        get().getPayrollTable(data.month, data.year);
      }
      return res;
    } catch (error) {
      set({ updatingPayrollError: error });
      throw error;
    } finally {
      set({ updatingPayroll: false });
    }
  },

  deleteMonthlyPayroll: async (id, month, year) => {
    set({ deletingPayroll: true, deletingPayrollError: {}, deletedPayroll: null });
    try {
      const res = await axios.delete(`/payroll/${id}`);
      set({ deletedPayroll: res });
      get().getAllPayrolls();
      if (month && year) {
        get().getPayrollTable(month, year);
      }
    } catch (error) {
      set({ deletingPayrollError: error });
    } finally {
      set({ deletingPayroll: false });
    }
  },

  updateLocalStatus: (id, status) => {
    const currentPayrolls = get().payrolls;
    let updatedData = [];
    
    if (currentPayrolls.data && Array.isArray(currentPayrolls.data)) {
      updatedData = currentPayrolls.data.map(p => p.id === id ? { ...p, status } : p);
      set({ payrolls: { ...currentPayrolls, data: updatedData } });
    } else if (Array.isArray(currentPayrolls)) {
      updatedData = currentPayrolls.map(p => p.id === id ? { ...p, status } : p);
      set({ payrolls: updatedData });
    }

    const currentDetails = get().payrollDetails;
    if (currentDetails && currentDetails.id === id) {
      set({ payrollDetails: { ...currentDetails, status } });
    }
  },

  approveMonthlyPayroll: async (id, month, year) => {
    set({ approvingPayroll: true, approvingPayrollError: {}, approvedPayroll: null });
    try {
      const res = await axios.patch(`/payroll/approve/${id}`);
      set({ approvedPayroll: res });
      
      get().updateLocalStatus(id, "approved");
      
      get().getAllPayrolls();
      if (month && year) {
        get().getPayrollTable(month, year);
      }
      get().getPayrollDetails(id);
    } catch (error) {
      set({ approvingPayrollError: error });
    } finally {
      set({ approvingPayroll: false });
    }
  },

  checkMonthlyPayroll: async (id, month, year) => {
    set({ checkingPayroll: true, checkingPayrollError: {}, checkedPayroll: null });
    try {
      const res = await axios.patch(`/payroll/check/${id}`);
      set({ checkedPayroll: res });
      
      get().updateLocalStatus(id, "checked");
      
      get().getAllPayrolls();
      if (month && year) {
        get().getPayrollTable(month, year);
      }
      get().getPayrollDetails(id);
    } catch (error) {
      set({ checkingPayrollError: error });
    } finally {
      set({ checkingPayroll: false });
    }
  },

  rejectMonthlyPayroll: async (id, month, year) => {
    set({ rejectingPayroll: true, rejectingPayrollError: {}, rejectedPayroll: null });
    try {
      const res = await axios.patch(`/payroll/reject/${id}`);
      set({ rejectedPayroll: res });
      
      get().updateLocalStatus(id, "rejected");
      
      get().getAllPayrolls();
      if (month && year) {
        get().getPayrollTable(month, year);
      }
      get().getPayrollDetails(id);
    } catch (error) {
      set({ rejectingPayrollError: error });
    } finally {
      set({ rejectingPayroll: false });
    }
  },

  undoApproveMonthlyPayroll: async (id, month, year) => {
    set({ undoingApprovePayroll: true, undoingApprovePayrollError: {}, undoneApprovePayroll: null });
    try {
      const res = await axios.patch(`/payroll/undo-approve/${id}`);
      set({ undoneApprovePayroll: res });
      
      get().updateLocalStatus(id, "pending");
      
      get().getAllPayrolls();
      if (month && year) {
        get().getPayrollTable(month, year);
      }
      get().getPayrollDetails(id);
    } catch (error) {
      set({ undoingApprovePayrollError: error });
    } finally {
      set({ undoingApprovePayroll: false });
    }
  },

  downloadPayrollPdf: async (id) => {
    set({ downloadingPayrollPdf: true, downloadingPayrollPdfError: {} });
    try {
      // Single payslip PDF download (backend: GET /api/v1/payroll/:id/download-pdf)
      const response = await axios.get(`/payroll/${id}/download-pdf`, {
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Payroll_${id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      set({ downloadingPayrollPdfError: error });
      throw error;
    } finally {
      set({ downloadingPayrollPdf: false });
    }
  },

  setCreatingPayroll: (show = false, creating = false, record = null) => {
    set({
      showCreatePayroll: show,
      creating: creating,
      payrollToEdit: record,
    });
  },

  setCreatingPayrollPeriod: (show = false) => {
    set({
      showCreatePayrollPeriod: show,
      creatingPayrollPeriod: show,
    });
  },

  setPayrollPeriod: (period) => {
    set({
      currentPayrollPeriod: period,
      payrollPeriodCreated: period,
    });
  },

  setShowPayrollDetails: (show = false, record = null) => {
    set({
      showPayrollDetails: show,
      payrollDetailsRecord: record,
    });
  },
});

const usePayrollStore = create(devtools(store));
export default usePayrollStore;
